/**
 * App-wide preferences (OpenDyslexic toggle) persisted to localStorage and
 * reflected on the <html> element so the CSS `[data-dyslexic="on"]` rule swaps
 * the reader font.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { PreferencesContext } from "@/app/preferencesContext";

const STORAGE_KEY = "creative-reading:dyslexic";

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [dyslexicFont, setDyslexicFont] = useState<boolean>(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(STORAGE_KEY) === "on";
  });

  useEffect(() => {
    document.documentElement.dataset.dyslexic = dyslexicFont ? "on" : "off";
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, dyslexicFont ? "on" : "off");
    }
  }, [dyslexicFont]);

  const toggleDyslexicFont = useCallback(
    () => setDyslexicFont((v) => !v),
    [],
  );

  const value = useMemo(
    () => ({ dyslexicFont, toggleDyslexicFont }),
    [dyslexicFont, toggleDyslexicFont],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}
